<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import {
  LayoutDashboard,
  MessageSquare,
  Bot,
  Sparkles,
  Wrench,
  ShieldCheck,
  BookOpen,
  Layers,
  ScrollText,
  Radio,
  Brain,
  KeyRound,
  MessagesSquare,
} from 'lucide-vue-next'

/**
 * 侧边栏 —— 对齐 docs/01-业务需求/UI.pen 中 Sidebar 组件
 * 10 个导航项分两段：核心 / 治理 & 行业
 */
interface NavItem {
  to: string
  label: string
  icon: unknown
  matches?: RegExp
}

const coreItems: NavItem[] = [
  { to: '/dashboard', label: '工作台', icon: LayoutDashboard },
  { to: '/chat', label: '会话中心', icon: MessageSquare, matches: /^\/chat/ },
  { to: '/memories', label: '我的记忆', icon: Brain },
  { to: '/agents', label: '智能体', icon: Bot, matches: /^\/agents/ },
  { to: '/embed-apps', label: '嵌入应用', icon: KeyRound, matches: /^\/embed-apps/ },
  { to: '/embed-sessions', label: '嵌入会话', icon: MessagesSquare, matches: /^\/embed-sessions/ },
  { to: '/skills', label: '技能目录', icon: Sparkles },
  { to: '/tools', label: '工具管理', icon: Wrench },
]

const govItems: NavItem[] = [
  { to: '/approvals', label: '审批流', icon: ShieldCheck },
  { to: '/kb', label: '知识库', icon: BookOpen },
  { to: '/models', label: '模型网关', icon: Layers },
  { to: '/audit', label: '审计中心', icon: ScrollText },
  { to: '/iot/devices', label: 'IoT 设备', icon: Radio, matches: /^\/iot/ },
]

const route = useRoute()
const auth = useAuthStore()

function isActive(item: NavItem) {
  if (item.matches) return item.matches.test(route.path)
  return route.path === item.to
}

const userInitials = computed(() => {
  if (auth.user?.avatarInitials) return auth.user.avatarInitials
  const name = auth.user?.displayName || auth.user?.username || 'U'
  return name.slice(0, 2).toUpperCase()
})
</script>

<template>
  <aside
    class="flex h-full w-60 shrink-0 flex-col border-r border-border bg-bg-surface px-4 py-5"
  >
    <!-- 品牌 -->
    <div class="flex items-center gap-2.5 px-1.5 pb-5">
      <div
        class="flex h-8 w-8 items-center justify-center rounded-lg bg-fg-primary font-mono font-bold text-accent-cyan"
      >
        &gt;_
      </div>
      <div class="flex flex-col leading-none">
        <span class="text-sm font-bold tracking-tight text-fg-primary">定格(Static)</span>
        <span class="mt-0.5 font-mono text-[10px] font-medium text-fg-muted">MVP · v0.1</span>
      </div>
    </div>

    <!-- 核心分组 -->
    <div class="mt-1 mb-1.5 px-1 font-mono text-[10px] font-bold tracking-[1.2px] text-fg-muted">
      核心
    </div>
    <nav class="flex flex-col gap-0.5">
      <RouterLink
        v-for="item in coreItems"
        :key="item.to"
        :to="item.to"
        :class="[
          'flex h-10 items-center gap-3 rounded-lg px-3.5 text-sm font-medium transition-colors',
          isActive(item)
            ? 'bg-primary-soft font-semibold text-primary'
            : 'text-fg-secondary hover:bg-bg-subtle',
        ]"
      >
        <component :is="item.icon" class="h-[18px] w-[18px] shrink-0" />
        <span>{{ item.label }}</span>
      </RouterLink>
    </nav>

    <!-- 治理 & 行业分组 -->
    <div
      class="mt-5 mb-1.5 px-1 font-mono text-[10px] font-bold tracking-[1.2px] text-fg-muted"
    >
      治理 & 行业
    </div>
    <nav class="flex flex-col gap-0.5">
      <RouterLink
        v-for="item in govItems"
        :key="item.to"
        :to="item.to"
        :class="[
          'flex h-10 items-center gap-3 rounded-lg px-3.5 text-sm font-medium transition-colors',
          isActive(item)
            ? 'bg-primary-soft font-semibold text-primary'
            : 'text-fg-secondary hover:bg-bg-subtle',
        ]"
      >
        <component :is="item.icon" class="h-[18px] w-[18px] shrink-0" />
        <span>{{ item.label }}</span>
      </RouterLink>
    </nav>

    <!-- 撑开 -->
    <div class="flex-1" />

    <!-- 用户卡片 -->
    <div class="flex items-center gap-2.5 rounded-[10px] bg-bg-subtle p-2.5">
      <div
        class="flex h-8 w-8 items-center justify-center rounded-full bg-primary-soft font-bold text-xs text-primary"
      >
        {{ userInitials }}
      </div>
      <div class="flex min-w-0 flex-col leading-tight">
        <span class="truncate text-sm font-semibold text-fg-primary">
          {{ auth.user?.username ?? '未登录' }}
        </span>
        <span class="truncate text-[11px] text-fg-tertiary">
          {{ auth.user?.role ?? '—' }}
        </span>
      </div>
    </div>
  </aside>
</template>
